import { SnackbarOrigin } from "@mui/material/Snackbar";
import { t } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { Resource } from "@redotech/util/resource";
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from "notistack";
import { ReactNode } from "react";
import * as alertStyles from "./alert.module.css";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import { Flex } from "./flex";
import {
  enqueueNotification,
  NotificationInstance,
  popNotification,
} from "./notification-provider";

const defaultOrigin: SnackbarOrigin = {
  horizontal: "center",
  vertical: "bottom",
};
const defaultDuration = Temporal.Duration.from({ seconds: 5 });

function close(snackbarId: SnackbarKey) {
  return (
    <RedoButton
      aria-label={t("Dismiss")}
      buttonClassName={alertStyles.toastCloseButton}
      hierarchy="tertiary"
      IconLeading={XIcon}
      onClick={(event) => {
        closeSnackbar(snackbarId);
        event.stopPropagation();
      }}
      size="xs"
    />
  );
}

export function alertOnFailure(
  message?: string,
  hideErrorMessage = false,
): Resource<void> {
  return async (fn) => {
    try {
      return await fn();
    } catch (error) {
      const alertMessage = `${message ?? ""}${!hideErrorMessage && error instanceof Error && !!error.message ? `${message ? ": " : ""}${error.message}` : ""}`;
      console.error(error);
      enqueueSnackbar(alertMessage, {
        anchorOrigin: defaultOrigin,
        autoHideDuration: defaultDuration.total("milliseconds"),
        variant: "error",
        action: close,
      });
      throw error;
    }
  };
}

export function alertMessage(message: string): void {
  enqueueSnackbar(message, {
    anchorOrigin: defaultOrigin,
    autoHideDuration: defaultDuration.total("milliseconds"),
    variant: "error",
    action: close,
  });
}

export const showNotification = (instance: NotificationInstance) =>
  enqueueNotification(instance);

export const hideNotification = () => popNotification();

// SnackbarOrigin itself trips a build error, so this mirror stays local.
interface ISnackbarOrigin {
  horizontal: "left" | "center" | "right";
  vertical: "top" | "bottom";
}

// Latest dismissPrevious toast wins; toasts are global and sequential.
let lastToastKey: SnackbarKey | null = null;

export function toast(
  message: string | ReactNode,
  options: {
    variant: "error" | "info" | "success" | "warning";
    origin?: ISnackbarOrigin;
    duration?: Temporal.Duration;
    persist?: boolean;
    hideIcon?: boolean;
    shake?: boolean;
    action?: ReactNode | ((dismiss: () => void) => ReactNode);
    dismissPrevious?: boolean;
  },
): { close: () => void } {
  if (options.dismissPrevious && lastToastKey !== null) {
    closeSnackbar(lastToastKey);
  }

  const key = enqueueSnackbar(message, {
    anchorOrigin: options.origin ?? defaultOrigin,
    autoHideDuration: options.persist
      ? null
      : (options.duration?.total("milliseconds") ??
        defaultDuration.total("milliseconds")),
    variant: options.variant,
    action: options.action
      ? (snackbarId: SnackbarKey) => {
          const dismiss = () => closeSnackbar(snackbarId);
          return (
            <Flex align="center" dir="row" gap="md">
              {typeof options.action === "function"
                ? options.action(dismiss)
                : options.action}
              {close(snackbarId)}
            </Flex>
          );
        }
      : close,
    hideIconVariant: options.hideIcon ?? false,
    className: options.shake ? alertStyles.shake : undefined,
  });

  if (options.dismissPrevious) {
    lastToastKey = key;
  }
  return { close: () => closeSnackbar(key) };
}
